<template>
  <page-header title="Statistics Dashboard">
    <button type="button" class="btn-primary ml-auto h-10" @click="download">
      <DocumentArrowDownIcon class="size-5 -ml-1 mr-2" />
      Export
    </button>
  </page-header>

  <div class="container mx-auto p-6">
    <div class="mb-6 bg-white p-4 rounded-lg shadow">
      <div class="flex gap-4">
        <div class="flex-1">
          <label class="block text-sm font-medium text-gray-700">Section</label>
          <select
            v-model="filters.section"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            @change="fetchData"
          >
            <option v-for="(label, value) in availableSections" :key="value" :value="value">
              {{ label }}
            </option>
          </select>
        </div>
        <div class="flex-1">
          <label class="block text-sm font-medium text-gray-700">Start Date (UTC)</label>
          <input
            v-model="filters.from"
            type="datetime-local"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            @change="fetchData"
          >
        </div>
        <div class="flex-1">
          <label class="block text-sm font-medium text-gray-700">End Date (UTC)</label>
          <input
            v-model="filters.to"
            type="datetime-local"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            @change="fetchData"
          >
        </div>
      </div>
    </div>

    <div class="relative">
      <transition
        enter-active-class="transition ease-out"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
        appear
      >
        <div v-if="isLoading" class="flex items-center justify-center mt-36 absolute inset-0">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500" />
        </div>

        <div v-else-if="filters.section === 'session'" class="absolute inset-0">
          <stats-sessions :sessions="statsData" :session-duration="sessionDuration" :format-time="formatTime" :is-session-analytics="!user || user.tier === 'session-analytics'" />
        </div>

        <div v-else-if="filters.section === 'user'" class="absolute inset-0">
          <stats-users :users="statsData" :format-time="formatTime" :is-session-analytics="!user || user.tier === 'session-analytics'" />
        </div>

        <div v-else-if="filters.section === 'program'" class="absolute inset-0">
          <stats-programs :programs="programsData" :stats="programStats" />
        </div>

        <div v-else-if="filters.section === 'subscription'" class="absolute inset-0">
          <stats-subscriptions :subscriptions="statsData" :format-time="formatTime" />
        </div>

        <div v-else-if="filters.section === 'stripe'" class="absolute inset-0">
          <stats-stripe :stats="statsData" />
        </div>

        <div v-else class="hidden">
          How does it happens?
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import duration from 'dayjs/plugin/duration';
import PageHeader from '../components/PageHeader.vue';
import StatsSessions from '../components/StatsSessions.vue';
import StatsUsers from '../components/StatsUsers.vue';
import StatsPrograms from '../components/StatsPrograms.vue';
import StatsSubscriptions from '../components/StatsSubscriptions.vue';
import StatsStripe from '../components/StatsStripe.vue';
import { useToast } from 'vue-toastification';
import { DocumentArrowDownIcon } from '@heroicons/vue/24/outline';
import * as XLSX from 'xlsx';
import { formatDate } from '../utils/formatters';
import { getUser } from '../composables/useAuth';

dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);

const TIMEZONE = 'UTC';

const toast = useToast();

const user = getUser();

const sections = {
  'admin': {
    session: 'Sessions',
    user: 'Users',
    program: 'Programs',
    subscription: 'Subscriptions',
    stripe: 'Stripe'
  },
  'seo': {
    session: 'Sessions',
    user: 'Users',
    program: 'Programs',
    subscription: 'Subscriptions',
    stripe: 'Stripe'
  },
  'session-analytics': {
    session: 'Sessions',
    user: 'Users'
  }
};

const availableSections = sections[user.value.tier] || {};

// Initialize with UTC: start of day (00:00) to current time
function getInitialDateRange() {
  const now = dayjs.utc();
  const startOfDay = now.startOf('day');
  return {
    from: startOfDay.format('YYYY-MM-DDTHH:mm'),
    to: now.format('YYYY-MM-DDTHH:mm')
  };
}

const { from, to } = getInitialDateRange();

const section = 'session';

const filters = reactive({ from, to, section });
const statsData = ref([]);
const programsData = ref([]);
const programStats = ref({});
const isLoading = ref(false);

async function fetchData() {
  const minLoadingTime = 350; // ms, make animation smoother
  const loadingStart = Date.now();

  const utcFrom = dayjs.utc(filters.from).format();
  const utcTo = dayjs.utc(filters.to).format();

  try {
    isLoading.value = true;

    if (filters.section === 'program') {
      const [ programsResponse, statsResponse ] = await Promise.all([
        fetch(`/api/stat/program?from=${utcFrom}&to=${utcTo}`),
        fetch('/api/program/stats')
      ]);

      programsData.value = await programsResponse.json();
      const statsResult = await statsResponse.json();
      programStats.value = {
        total: statsResult.total,
        free: statsResult.free,
        paid: statsResult.paid,
        categories: statsResult.categories
      };
    } else {
      const response = await fetch(
        `/api/stat/${filters.section}?from=${utcFrom}&to=${utcTo}`
      );
      statsData.value = await response.json();
    }

  } catch (error) {
    console.error('Error fetching data:', error);
    toast.error('Error fetching data');

  } finally {
    const loadTime = Date.now() - loadingStart;
    setTimeout(() => {
      isLoading.value = false;
    }, Math.max(0, minLoadingTime - loadTime));
  }
}

function formatTime(seconds) {
  return dayjs.duration(seconds, 'seconds').format('HH:mm:ss');
}

function sessionDuration(session) {
  return dayjs(session.finishedAt).diff(dayjs(session.startedAt), 'second');
}

function download() {
  if (!statsData.value.length) {
    toast.info('No data to export');
    return;
  }

  const data = parseData();

  const ws = XLSX.utils.json_to_sheet(data);
  ws['!cols'] = fitToColumn(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, `${filters.section}s`);

  const a = document.createElement('a');
  const url = URL.createObjectURL(new Blob([ XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) ]));
  a.href = url;
  a.download = `${filters.section}s_${filters.from}_${filters.to}.xlsx`;
  a.click();
}

function parseData() {
  if (filters.section === 'session') {
    if (user.value.tier === 'session-analytics') {
      return statsData.value.map(session => ({
        'userId': session.id,
        'sessionId': session.sessionId,
        'time': sessionDuration(session),
        'program': session.program,
        'browser': session.browser || 'n/a',
        'resolution': session.resolution || 'n/a',
        'OS': session.os || 'n/a',
        'country': session.countryCode || 'n/a',
        'date': formatDateSpecial(session.startedAt)
      }));
    }

    return statsData.value.map(session => ({
      'userId': session.id,
      'sessionId': session.sessionId,
      'email': session.email,
      'session': 1, // NOTE: we know this is 1 because it's a single session
      'time': sessionDuration(session),
      'program': session.program,
      'plugins': session.plugins?.join(', ') || '',
      'isEmailConfirmed': true, // NOTE: we know this is true, because we are fetching sessions for logged in user
      'language': session.language || 'n/a',
      'loginMethod': session.loginMethod || 'n/a',
      'evaluation': session.evaluation || 'n/a',
      'feedback': session.feedback || 'n/a',
      'browser': session.browser || 'n/a',
      'resolution': session.resolution || 'n/a',
      'OS': session.os || 'n/a',
      'utm_campaign': session.utm_campaign || 'n/a',
      'utm_content': session.utm_content || 'n/a',
      'utm_medium': session.utm_medium || 'n/a',
      'utm_source': session.utm_source || 'n/a',
      'utm_term': session.utm_term || 'n/a',
      'country': session.countryCode || 'n/a',
      'date': formatDateSpecial(session.startedAt)
    }));
  }

  if (filters.section === 'user') {
    return statsData.value.map(_user => {
      if (user.value.tier === 'session-analytics') {
        return {
          'id': _user.id,
          'isEmailConfirmed': _user.isEmailConfirmed,
          'createdAt': formatDateSpecial(_user.createdAt),
          'language': _user.language || 'n/a',
          'loginMethod': _user.loginMethod || 'n/a',
          'countryCode': _user.countryCode || 'n/a',
          'programFirstLaunch': _user.programFirstLaunch || 'n/a',
          'isOnboarded': _user.isOnboarded
        };
      }

      return {
        'id': _user.id,
        'email': _user.email,
        'isEmailConfirmed': _user.isEmailConfirmed,
        'sessionCount': _user.sessionCount,
        'timeLeftSeconds': _user.timeLeftSeconds,
        'createdAt': formatDateSpecial(_user.createdAt),
        'language': _user.language || 'n/a',
        'loginMethod': _user.loginMethod || 'n/a',
        'utm_campaign': _user.utm_campaign || 'n/a',
        'utm_content': _user.utm_content || 'n/a',
        'utm_medium': _user.utm_medium || 'n/a',
        'utm_source': _user.utm_source || 'n/a',
        'utm_term': _user.utm_term || 'n/a',
        'countryCode': _user.countryCode || 'n/a',
        'programFirstLaunch': _user.programFirstLaunch || 'n/a',
        'isOnboarded': _user.isOnboarded,
        'onboardingOption': _user.onboardingOption,
        'onboardingDirection': _user.onboardingDirection,
        'onboardingPrograms': _user.onboardingPrograms,
        'onboardingQuestions': _user.onboardingQuestions,
        'onboardingAnswers': _user.onboardingAnswers,
        'storageUsage': _user.storageUsage
      };
    });
  }

  if (filters.section === 'subscription') {
    return statsData.value.map(subscription => ({
      'id': subscription.id,
      'email': subscription.email,
      'subscriptions': subscription.type,
      'date':   formatDateSpecial(subscription.startsAt),
      'campaign': subscription.utm_campaign || 'n/a',
      'group': subscription.utm_content || 'n/a',
      'medium': subscription.utm_medium || 'n/a',
      'keywords': subscription.utm_term || 'n/a',
      'country_code': subscription.countryCode || 'n/a',
      'country': subscription.country || 'n/a',
      'program': subscription.program || 'n/a',
      'status': subscription.status || 'n/a',
      'date_cancel_sub': subscription.status === 'cancel' ? formatDateSpecial(subscription.canceledAt) : null
    }));
  }

  if (filters.section === 'stripe') {
    return statsData.value;
  }

  return null;
}

function formatDateSpecial(date) {
  return formatDate(date, { format: 'YYYY-MM-DD HH:mm:ss', emptyPlaceholder: '', timezone: TIMEZONE, shortTimezone: '' });
}

function fitToColumn(data) {
  return data.reduce((acc, row) => {
    Object.keys(row).forEach((key, i) => {
      const len = Math.max(String(key).length, String(row[key]).length);
      if (!acc[i] || acc[i] < len) {
        acc[i] = len;
      }
    });
    return acc;
  }, []).map(len => ({ wch: len }));
}

// Reset date range to start of day (00:00) to current time when page is mounted/navigated to
onMounted(() => {
  const { from: newFrom, to: newTo } = getInitialDateRange();
  filters.from = newFrom;
  filters.to = newTo;
  fetchData();
});
</script>
